import { useRef, useState } from 'react'
import type React from 'react'

/**
 * Fraction of the sheet height a downward drag must pass to dismiss.
 * 0.3 == iOS `UISheetPresentationController` detent feel; shared verbatim
 * with the value StationDetailsModal shipped with (Bug 4 parity).
 */
const DISMISS_FRACTION = 0.3
/** Fast-flick escape hatch: a downward velocity past this dismisses even
 *  under the distance threshold. px per ms. */
const DISMISS_VELOCITY_PX_PER_MS = 0.6

export interface SwipeDownDismissDragHandlers {
  onPointerDown: (e: React.PointerEvent<HTMLElement>) => void
  onPointerMove: (e: React.PointerEvent<HTMLElement>) => void
  onPointerUp: (e: React.PointerEvent<HTMLElement>) => void
  onPointerCancel: (e: React.PointerEvent<HTMLElement>) => void
}

export interface SwipeDownDismiss {
  /** Live downward offset in px while dragging (0 at rest). */
  readonly dragOffset: number
  /** `true` while the release transition (spring-back / dismiss) plays. */
  readonly springing: boolean
  /** Inline `transform` for the sheet, or `undefined` when at rest. */
  readonly sheetStyle: { transform: string } | undefined
  /** Spread on the drag-zone element (the header pill area). */
  readonly handlers: SwipeDownDismissDragHandlers
}

/**
 * iOS-style swipe-down-to-dismiss gesture for a bottom sheet, extracted
 * from `StationDetailsModal` so `MobileRouteDetailsOverlay` reuses the
 * IDENTICAL threshold + animation contract (Bug 4 — RouteDetails phone
 * modal could not be swiped down, unlike StationDetails).
 *
 * Native PointerEvents only (no dependency). The drag is bound to the
 * sheet's header/handle zone; the content below scrolls normally.
 *
 * Dismiss when the release is past {@link DISMISS_FRACTION} of the sheet
 * height OR faster than {@link DISMISS_VELOCITY_PX_PER_MS}; otherwise the
 * sheet springs back to rest.
 *
 * @param sheetRef ref to the sheet element (used to measure its height for
 *   the distance threshold).
 * @param onDismiss called once when a release crosses either threshold.
 */
export function useSwipeDownDismiss(
  sheetRef: React.RefObject<HTMLElement | null>,
  onDismiss: () => void,
): SwipeDownDismiss {
  const dragStartRef = useRef<{ y: number; t: number; pointerId: number } | null>(null)
  const [dragOffset, setDragOffset] = useState(0)
  // `springing` toggles the CSS transition on/off. While dragging we want
  // raw 1:1 follow (no transition); on release we re-enable the transition
  // so either a dismiss-translateY or a spring-back-to-0 is animated.
  const [springing, setSpringing] = useState(false)

  const onPointerDown = (e: React.PointerEvent<HTMLElement>): void => {
    dragStartRef.current = { y: e.clientY, t: performance.now(), pointerId: e.pointerId }
    // Capture so subsequent move/up events come to us even if the pointer
    // leaves the drag zone. Feature-checked because jsdom (test env) does
    // not implement Element.setPointerCapture.
    if (typeof e.currentTarget.setPointerCapture === 'function') {
      e.currentTarget.setPointerCapture(e.pointerId)
    }
  }

  const onPointerMove = (e: React.PointerEvent<HTMLElement>): void => {
    if (dragStartRef.current === null) return
    const delta = e.clientY - dragStartRef.current.y
    // Clamp upward movement: the sheet can never lift above its resting
    // detent. Downward is unbounded — the sheet trails the pointer until
    // release. Disable the spring transition during raw pointer-following
    // so the sheet tracks the finger 1:1.
    if (springing) setSpringing(false)
    setDragOffset(delta < 0 ? 0 : delta)
  }

  const onPointerUp = (e: React.PointerEvent<HTMLElement>): void => {
    const start = dragStartRef.current
    dragStartRef.current = null
    if (start === null) return
    if (
      typeof e.currentTarget.hasPointerCapture === 'function' &&
      e.currentTarget.hasPointerCapture(e.pointerId)
    ) {
      e.currentTarget.releasePointerCapture(e.pointerId)
    }
    const delta = e.clientY - start.y
    const elapsed = Math.max(1, performance.now() - start.t)
    const velocity = delta / elapsed
    const sheetHeight = sheetRef.current?.getBoundingClientRect().height ?? 0
    const pastDistance = sheetHeight > 0 && delta > sheetHeight * DISMISS_FRACTION
    const pastVelocity = velocity > DISMISS_VELOCITY_PX_PER_MS
    setSpringing(true)
    if (pastDistance || pastVelocity) {
      onDismiss()
      return
    }
    setDragOffset(0)
  }

  const sheetStyle =
    dragOffset > 0 || springing ? { transform: `translateY(${dragOffset}px)` } : undefined

  return {
    dragOffset,
    springing,
    sheetStyle,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
    },
  }
}
